/**
   AuthService.js - Servizio centralizzato per la gestione dell'autenticazione
   ---------------------------------------------------------------------------
   
   Flussi di autenticazione:
   
   Il sistema verifica automaticamente la scadenza dell'access token prima di ogni richiesta API.
   
   1. PRIMO LOGIN (autenticazione con credenziali)
   
      A. Standard Auth:
      ┌─────────────┐     ┌────────────┐     ┌─────────────┐     ┌───────────┐
      │ Username,   │     │ Invia      │     │ Salva       │     │           │
      │ Password,   │ --> │ credenziali│ --> │ token in    │ --> │ DASHBOARD │
      │             │     │ a /login   │     │ localStorage│     │           │
      └─────────────┘     └────────────┘     └─────────────┘     └───────────┘
   
      B. Cognito Auth:
      ┌─────────────┐     ┌────────────┐     ┌─────────────┐     ┌───────────┐
      │  Username,  │     │ Autentica  │     │ Salva token │     │           │
      │  Password   │ --> │ tramite    │ --> │ in cookie e │ --> │ DASHBOARD │
      │             │     │ Cognito    │     │ localstorage│     │           │
      └─────────────┘     └────────────┘     └─────────────┘     └───────────┘
   
   2. LOGIN CON SESSIONE ESISTENTE (verifica token/cookie)
   
      A. Standard Auth:
      ┌─────────────┐     ┌────────────┐                         ┌───────────┐
      │ Verifica    │     │ Estrae     │                         │           │
      │ token in    │ --> │ dati utente│ ----------------------> │ DASHBOARD │
      │ localStorage│     │ dai token  │                         │           │
      └─────────────┘     └────────────┘                         └───────────┘
   
      B. Cognito Auth:
      ┌─────────────┐     ┌────────────┐     ┌─────────────┐     ┌───────────┐
      │  Verifica   │     │ Estrae     │     │ Crea        │     │           │
      │  token nei  │ --> │ dati utente│ --> │ sessione    │ --> │ DASHBOARD │
      │  cookie     │     │ dai token  │     │ Cognito     │     │           │
      └─────────────┘     └────────────┘     └─────────────┘     └───────────┘
   
   3. LOGOUT
   
      A. Standard Auth:
      ┌─────────────┐                                            ┌───────────┐
      │ Cancella    │                                            │           │
      │ dati da     │ -----------------------------------------> │ LOGIN     │
      │ localStorage│                                            │           │
      └─────────────┘                                            └───────────┘
   
      B. Cognito Auth:
      ┌─────────────┐     ┌────────────┐     ┌─────────────┐     ┌───────────┐
      │ Revoca      │     │ Cancella   │     │ Cancella    │     │           │
      │ refreshToken│ --> │localStorage│ --> │ cookie      │ --> │  LOGIN    │
      │ tramite API │     │ e signOut  │     │             │     │           │
      └─────────────┘     └────────────┘     └─────────────┘     └───────────┘
   
 */

import config from '../config';
import { STORAGE_KEYS, OTHER_KEYS } from './keys.js';
import {
     CognitoAccessToken
    ,CognitoIdToken
    ,CognitoRefreshToken
    ,CognitoUser
    ,CognitoUserPool
    ,CognitoUserSession
    ,AuthenticationDetails
} from 'amazon-cognito-identity-js';
import appinfo from '../appinfo';

const bDebug        = !!config?.DEBUG?.AuthService;
const DEFAULT_PSW   = 'Password01!';

const truncateToken = (str, n) => !str ? '' : ( str.length <= 2 * n ? str : `${str.slice(0, n)}...${str.slice(-n)}` );

// Configurazione userPool di Cognito (se abilitato)
const userPool      = ( config?.COGNITO_ENABLED !== 'Y' ) ? {} : (
    new CognitoUserPool({
         UserPoolId: config?.REACT_APP_AWS_POOL_ID       || ''
        ,ClientId:   config?.REACT_APP_AWS_APP_CLIENT_ID || ''
    })
);

// in fase di inizializzazione/login carica i dati dell'utente se aveva già qualcosa in memoria di Cognito
let currentUser = ( config?.COGNITO_ENABLED !== 'Y' ) ? {} : userPool.getCurrentUser();

/**
 * Esegue una richiesta fetch e gestisce il parsing JSON in modo sicuro
 * Viene usata questa authFetch anziché FetchService perché AuthService non deve dipendere da nient'altro!
 * Gestisce specificamente errori 401 e 404 con messaggio specifico
 *
 * @param {string} url - URL della richiesta
 * @param {Object} options - Opzioni per fetch (method, headers, body, ecc.)
 * @returns {Promise<Object>} Dati JSON in caso di successo
 * @throws {Error} Errore con messaggio specifico per 401 o generico 'Request failed' (per parsing JSON o risposta non valida)
 */
async function authFetch( url, options = {} ) {
    
    // if ( !response?.ok ) throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    let sExpectedErrorToShow = '';
    try {
        
        if ( !options.headers ) { options.headers = {}; }
        
        options.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        options.headers['Pragma'       ] = 'no-cache';
        options.headers['Expires'      ] = '0';
        options.cache                    = 'no-store'
        
        const response = await fetch( url, options );
        
        if ( response.status === 401 ) {
            sExpectedErrorToShow = 'Invalid credentials or user not enabled.';
            throw new Error( sExpectedErrorToShow );
        }
        
        if ( response.status === 404 ) {
            sExpectedErrorToShow = 'Server responded: requested path not found.';
            throw new Error( sExpectedErrorToShow );
        }
        
        return await response.json();
        
    } catch ( error ) {
        console.error(error);
        throw new Error( sExpectedErrorToShow || 'Connection or application error.' );
    }
}

/**
 * Ottiene il valore di un cookie dato il suo nome
 * @param {string} name - Nome del cookie da cercare
 * @returns {string|null} Valore del cookie o null se non trovato
 */
function getCookieByName( name ) {
    const cookieString  = document.cookie;
    const cookies       = cookieString.split(';');
    for ( let i = 0; i < cookies.length; i++ ) {
        const cookie = cookies[i].trim();
        if ( cookie.startsWith( name + '=' ) ) {
            return cookie.substring( name.length + 1 );
        }
    }
    return null;
}

/**
 * Imposta un cookie del browser con il nome e il valore specificati, insieme a parametri di configurazione opzionali.
 * Verifica che il cookie sia stato impostato correttamente.
 *
 * @param {string} name - Il nome del cookie da impostare
 * @param {string|number} value - Il valore da memorizzare nel cookie (verrà automaticamente codificato)
 * @param {Object} [options={}] - Parametri di configurazione opzionali per il cookie
 * @param {string} [options.path] - Il percorso sul server per il quale il cookie è valido
 * @param {string} [options.domain] - Il dominio per il quale il cookie è valido
 * @param {number} [options.maxAge] - L'età massima del cookie in secondi
 * @param {string|Date} [options.expires] - La data di scadenza del cookie (stringa UTC o oggetto Date)
 *                                          Se non specificata, il cookie scade a mezzanotte del giorno successivo
 * @param {boolean} [options.secure] - Quando è true, il cookie verrà inviato solo tramite HTTPS
 * @param {string} [options.sameSite] - Controlla quando i cookie vengono inviati con richieste cross-site
 *                                      (valori possibili: 'strict', 'lax' o 'none')
 * @param {boolean} [options.silent=false] - Se true, non verranno mostrati messaggi di log
 * @returns {boolean} - true se il cookie è stato impostato con successo, false altrimenti
 */
function setCookie( name, value, options = {} ) {
    try {
        // Salva il valore originale prima della codifica per la verifica
        const originalValue = value;
        
        // Costruisci la stringa del cookie come nella versione originale
        let cookieString    = `${ name }=${ encodeURIComponent(value) }`;
        
        if (!options.expires) {
            // se non viene specificata viene impostata fino a mezzanotte
            let today   = new Date();
            let nextDay = new Date(today);
            nextDay.setDate( today.getDate() + 1 ); // aggiunge un giorno
            nextDay.setHours( 0, 0, 0, 0 ); // imposta a mezzanotte
            options.expires = new Date(nextDay).toUTCString();
        }
        
        // Aggiungi gli attributi opzionali
        if ( options.path     ) cookieString += `; path=${     options.path     }`;
        if ( options.domain   ) cookieString += `; domain=${   options.domain   }`;
        if ( options.maxAge   ) cookieString += `; max-age=${  options.maxAge   }`;
        if ( options.expires  ) cookieString += `; expires=${  options.expires  }`; // deve essere toUTCString()
        if ( options.secure   ) cookieString += `; secure`;
        if ( options.sameSite ) cookieString += `; samesite=${ options.sameSite }`;
        
        bDebug && console.log(`Tentativo di impostare cookie name: "${ name }", path: "${ options?.path }", domain: "${ options?.domain }"`);
        
        // Imposta il cookie
        document.cookie = cookieString;
        
        // Verifica se il cookie è stato impostato correttamente
        const retrievedValue = getCookieByName(name);
        const decodedRetrievedValue = decodeURIComponent(retrievedValue);
        
        // Verifica se il cookie esiste e se il valore decodificato corrisponde all'originale
        const isSuccess = ( retrievedValue !== null ) && ( decodedRetrievedValue == originalValue );
        
        // Stampa i risultati della verifica se non in modalità silenziosa
        
            if ( isSuccess ) {
                bDebug && console.log(`Cookie "${ name }" impostato con successo.`);
            } else {
                if ( retrievedValue === null ) {
                    console.error(`Impossibile impostare il cookie "${ name }". Il cookie non è stato trovato dopo l'impostazione.`);
                } else {
                    console.error(`Valore impostato e recuperato non corrispondono per "${ name }".`);
                    bDebug && console.log(`Originale:  "${ originalValue }"`);
                    bDebug && console.log(`Recuperato: "${ decodedRetrievedValue }"`);
                }
                
                // Aggiungi informazioni diagnostiche aggiuntive
                
                console.warn(`Il dominio specificato "${ options?.domain }" potrebbe non corrispondere al dominio corrente o essere in un formato non valido.`);

                if (options?.secure && window.location.protocol !== 'https:') {
                    console.warn(`Il flag "secure" è impostato ma la pagina non è caricata tramite HTTPS.`);
                }
                if (options?.sameSite === 'none' && !options?.secure) {
                    console.warn(`"SameSite=None" richiede che il flag "secure" sia impostato.`);
                }
            }
        
        return isSuccess;
        
    } catch (e) {
        console.error(`Errore durante l'impostazione del cookie "${ name }":`, e);
        return false;
    }
}

/**
 * Elimina un cookie dal browser cercandolo per nome.
 * Se forniti, utilizza path e domain specifici
 *
 * @param {string} sCookieName - Il nome del cookie da eliminare
 * @param {string|null} sPath - Path specifico del cookie (opzionale)
 * @param {string|null} sDomain - Domain specifico del cookie (opzionale)
 * @returns {boolean} - true se il cookie è stato eliminato con successo, false altrimenti
 */
function deleteCookieByName( sCookieName, sPath, sDomain ) {
    
    if ( !sDomain ) {
        console.error(`deleteCookieByName: Domain "${ sDomain }" non specificato`);
        return false;
    }
    
    try {
        
        // Funzione helper per verificare se un cookie esiste
        const cookieExists = name => document.cookie.split(';').some( c => c.trim().startsWith( name + '=' ) );
        
        // Funzione helper per tentare l'eliminazione con parametri specifici
        const attemptDelete = ( name, path, domain ) => {
            
            const sDataNelPassato = 'Thu, 01 Jan 1970 00:00:00 UTC';
            let sCookieString = `${ name }=; expires=${ sDataNelPassato }`;
            
            if ( path   ) sCookieString += `; path=${ path }`;
            if ( domain ) sCookieString += `; domain=${ domain }`;
            
            document.cookie = sCookieString; // imposta il cookie
            
            return !cookieExists( name ); // Verifica se il cookie è stato eliminato
            
        };
        
        // Verifica iniziale esistenza cookie
        if ( !cookieExists( sCookieName ) ) {
            console.error(`deleteCookieByName: Cookie "${ sCookieName }" non trovato`);
            return false;
        }
        bDebug && console.log(`deleteCookieByName: Cookie "${ sCookieName }" trovato, inizio eliminazione.`);
        
        // Caso 1: Se sono stati specificati parametri personalizzati, li usiamo
        if ( sPath && attemptDelete( sCookieName, sPath, sDomain ) ) {
            bDebug && console.log(`deleteCookieByName: Cookie "${ sCookieName }" eliminato con successo usando path "${ sPath }" e domain "${ sDomain }"`);
            return true;
        }
        bDebug && console.warn(`deleteCookieByName: Eliminazione fallita con parametri forniti path "${ sPath }" e domain "${ sDomain }".`);
        
        // Caso 2: Tentativo con path="/" (caso più comune)
        if ( attemptDelete( sCookieName, '/', sDomain ) ) {
            bDebug && console.log(`deleteCookieByName: Cookie "${ sCookieName }" eliminato con successo usando path "/" e domain "${ sDomain }".`);
            return true;
        }
        bDebug && console.warn(`deleteCookieByName: Eliminazione fallita con path "/" e domain "${ sDomain }".`);
        
        // Caso 3: Tentativo con il percorso corrente
        const pathCorrente = window.location.pathname;
        if ( attemptDelete( sCookieName, pathCorrente, sDomain ) ) {
            bDebug && console.log(`deleteCookieByName: Cookie "${ sCookieName }" eliminato con successo usando path "${ pathCorrente }" e domain "${ sDomain }".`);
            return true;
        }
        bDebug && console.warn(`deleteCookieByName: Eliminazione fallita con path "${ pathCorrente }" e domain "${ sDomain }".`);
        
        // Caso 4: Tentativo con la directory corrente (default per cookie senza path specificato)
        const pathDirectory = pathCorrente.substring(0, pathCorrente.lastIndexOf('/') + 1);
        if ( attemptDelete( sCookieName, pathDirectory, sDomain ) ) {
            bDebug && console.log(`deleteCookieByName: Cookie "${ sCookieName }" eliminato con successo usando path "${ pathDirectory }" e domain "${ sDomain }".`);
            return true;
        }
        
        // Nessun tentativo ha funzionato
        console.error(`deleteCookieByName: Non è stato possibile eliminare il cookie "${ sCookieName }".`);
        return false;
        
    } catch (e) {
        console.error(e);
        return false;
    }
    
}


/**
 * Servizio di autenticazione centralizzato che gestisce tutti i flussi di autenticazione (JWT e Cognito)
 */
class AuthService {
    
    /**
     * Gestisce il processo di login iniziale (con credenziali o token)
     * e reindirizza al flusso appropriato (Roobeek backend o Cognito)
     * @param {Object} credentials - Credenziali per il login
     * @param {string} [credentials.username] - Nome utente
     * @param {string} [credentials.password] - Password
     * @param {[string]} [credentials.asTokens] - array di Token di autenticazione
     * @returns {Promise<Object>} Dati utente in caso di successo
     */
    async login( credentials ) {
        
        bDebug && console.log('AuthService > login');
        
        // gli errori vengono propagati al livello superiore
        const { username, password, asTokens } = credentials;
        
        if ( asTokens ) {
            bDebug && console.log('login with token');
            // Se vengono forniti token (Cognito o JWT)
            // si utilizzano per l'autenticazione chiamando il backend di Roobeek per decodificarli e verificare validità
            // e viene restituito token JWT (che viene salvato in localstorage)
            return await this.loginWithRoobeekBackend( { asTokens } );
            
        } else if ( username && password ) {
            bDebug && console.log('login with username');
            // TODO: QUESTO ANDREBBE ABILITATO SOLO SE VOGLIAMO PERMETTERE AUTENTICAZIONE COGNITO CON USERNAME E PASSWORD
            // if ( config?.COGNITO_ENABLED === 'Y' ) {
            //     return await this.loginCognitoWithCredentials( { username, password } );
            //     
            // } else {
            
            // login standard con credenziali diretto al backend di Roobeek che restituisce token JWT (che viene salvato in localstorage)
            return await this.loginWithRoobeekBackend( { username, password } );
            
            // }
            
        } else {
            console.error('Dati obbligatori per login mancanti');
            // è solo per sicurezza, non arriva qui perché i controlli vengono fatti prima (esternamente a questa funzione "login")
            throw new Error('Invalid credentials'); 
        }
        
    }
    
    /**
     * Esegue login tramite il backend Roobeek
     * @param {Object} body - Oggetto contenente credenziali o tokens di autenticazione
     * @returns {Promise<Object>} Dati utente in caso di successo
     */
    async loginWithRoobeekBackend( body ) {
        
        body.frontendversion = appinfo?.VERSION; // aggiungo la versione attuale del frontend alla chiamata per la login
        // nel caso la versione del frontend non sia quella prevista dal backend verrà restituito errore 500
        // 'Roobeek outdated version detected, please reload this page or close your browser and reopen it.'
        
        // gli errori vengono propagati al livello superiore
        const oResponse = await authFetch(`${config.API_URL}/login`, {
             method:    'POST'
            ,headers:   { 'Accept': 'application/json', 'Content-Type': 'application/json' }
            ,body:      JSON.stringify( body ) // '{ "username":"miouser", "password":"miapsw" }' oppure '{ "asTokens":["adfAaDF34...","jhk34j..."] }'
        });
        /* esempio di risposta:
            {
                 success     : true
                ,token       : 'as2dfd3fas4dfas5df'
                ,kUser       : 123
                ,username    : 'miousername'
                ,fullname    : 'miofullname'
                ,privileges  : "{\"FLAG_ADMIN\":\"Y\"}" // stringa JSON
            }
        */
        
        if ( oResponse?.success ) {
            bDebug && console.log('login successful');
            // QUI salva in localstorage il nuovo token JWT arrivato da backend
            this.storeUserData( oResponse ); // in loginWithRoobeekBackend
            return oResponse;
        }
        
        throw new Error( oResponse?.error || 'Authentication failed' );
        
    }
    
    /**
     * Esegue login utilizzando Cognito (fornendo username e password)
     * @param {Object} credentials - Credenziali per il login
     * @param {string} [credentials.username] - Nome utente
     * @param {string} [credentials.password] - Password
     * @returns {Promise<Object>} Dati utente in caso di successo
     */
    loginCognitoWithCredentials( { username, password } ) {
        // gli errori vengono propagati al livello superiore
        return new Promise((resolve, reject) => {
            
            const authenticationDetails = new AuthenticationDetails({
                 Username:  username
                ,Password:  password
            });
            
            const cognitoUser = new CognitoUser({
                 Username:  username
                ,Pool:      userPool
            });
            
            // Funzione helper per gestire i token e creare i dati utente
            const handleSuccessfulAuth = ( cognitoResponse ) => {
                try {
                    
                    // Estrazione token dalla risposta
                    const sAccessToken      = cognitoResponse.getAccessToken().getJwtToken();
                    const sIdToken          = cognitoResponse.getIdToken().getJwtToken();
                    const sRefreshToken     = cognitoResponse.getRefreshToken().getToken();
                    
                    // Decodifica il payload per ottenere le informazioni
                    const oUserInfoDaToken  = cognitoResponse.getIdToken().decodePayload();
                    
                    /* esempi contenuti token leggibili
                        
                        ID TOKEN
                        {
                           "sub"                :  "d8e28284-5a23-48b7-89c1-c9b5430bbd76"   // ID univoco dell'utente
                          ,"aud"                :  "6m4plmn0ihaqi8o5jsl12cucm9"             // ID del client dell'app
                          ,"email_verified"     :  true
                          ,"event_id"           :  "aaa123bb-4cde-5f6g-7h8i-9j0k1l2m3n4o"
                          ,"token_use"          :  "id"                                     // Indica che questo è un ID token
                          ,"auth_time"          :  1615365574                               // Timestamp dell'autenticazione
                          ,"iss"                :  "https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_aB1cDe2Fg",   // Chi ha emesso il token
                          ,"cognito:username"   :  "mionome.miocognome"
                          ,"exp"                :  1615369174                               // Timestamp di scadenza
                          ,"iat"                :  1615365574                               // Timestamp di emissione
                          ,"email"              :  "mionome.miocognome@example.com"
                          ,"name"               :  "mionome miocognome"
                          ,"given_name"         :  "mionome"
                          ,"family_name"        :  "miocognome"
                          ,"phone_number"       :  "+39..."
                          ,"picture"            :  "https://example.com/photos/mionome.jpg"
                          ,"custom:role"        :  "admin"
                          ,"custom:company"     :  "MYCOMPANY Inc."
                          ,"custom:employeeId"  :  "EMP12345"
                        }
                        
                        ACCESS TOKEN
                        {
                           "sub"                : "d8e28284-5a23-48b7-89c1-c9b5430bbd76"
                          ,"device_key"         : "eu-west-1_ac1b3456-7890-1234-5678-9012def34567"
                          ,"event_id"           : "aaa123bb-4cde-5f6g-7h8i-9j0k1l2m3n4o"
                          ,"token_use"          : "access"                                               // Indica che questo è un access token
                          ,"scope"              : "openid email profile aws.cognito.signin.user.admin"   // Scopes autorizzati
                          ,"auth_time"          : 1615365574
                          ,"iss"                : "https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_aB1cDe2Fg"
                          ,"exp"                : 1615369174
                          ,"iat"                : 1615365574
                          ,"jti"                : "abcdef12-3456-7890-abcd-ef1234567890"                 // ID univoco del token
                          ,"client_id"          : "6m4plmn0ihaqi8o5jsl12cucm9"
                          ,"username"           : "mionome.miocognome"
                          ,"origin_jti"         : "abcdef12-3456-7890-abcd-ef1234567890"                  // ID originale se rinnovato
                        }
                        
                    */
                    
                    // Memorizzazione token in localStorage
                    localStorage.setItem( config?.TOKEN_NAMES?.RefreshToken ,sAccessToken  );
                    localStorage.setItem( config?.TOKEN_NAMES?.AccessToken  ,sIdToken      );
                    localStorage.setItem( config?.TOKEN_NAMES?.IdToken      ,sRefreshToken );
                    
                    // Creazione oggetto con dati utente
                    const oUserData = {
                         token:      sAccessToken
                        ,kUser:      oUserInfoDaToken['custom:profile'] + ''
                        ,username
                        ,fullname:   oUserInfoDaToken['name']
                        ,privileges: '{}' // TODO verificare come recuperare da DB. stringa JSON valido per ora vuoto. es. "{\"FLAG_ADMIN\":\"Y\"}"
                    };
                    
                    this.storeUserData( oUserData ); // in loginCognitoWithCredentials
                    resolve( oUserData );
                    
                } catch(err) {
                    console.error(err);
                    reject( err );
                }
            };
            
            cognitoUser.authenticateUser( authenticationDetails, {
                
                // Autenticazione avvenuta con successo
                onSuccess: handleSuccessfulAuth
                
                // Autenticazione fallita
                ,onFailure: ( err ) => {
                    console.error( 'Cognito authentication error:', err );
                    reject( err );
                }
                
                // Richiesta di nuova password (primo accesso o password scaduta)
                ,newPasswordRequired: function( userAttributes ) { // requiredAttributes
                        
                    // Rimuovi attributi non necessari che potrebbero causare errori
                    if ( userAttributes?.email_verified !== undefined ) {
                        delete userAttributes.email_verified;
                    }
                        
                    // Gestione cambio password forzato
                    cognitoUser.completeNewPasswordChallenge(
                        DEFAULT_PSW, 
                        userAttributes, 
                        {
                            onSuccess: handleSuccessfulAuth,
                            onFailure: ( err ) => {
                                console.error( 'Password challenge error:', err );
                                reject( err );
                            }
                        }
                    );
                }
                
                // Gestione MFA se necessario
                // ,mfaRequired: ( challengeName, challengeParameters ) => {
                //     console.error( 'MFA required but not implemented' );
                //     reject( new Error( 'MFA authentication not implemented' ) );
                // }
                
            });
        });
    }
    
    
    /**
     * Funzione helper interna per verificare la validità di una sessione Cognito
     * e sincronizzare i token su localStorage e cookie se valida
     *
     * @param {CognitoUser} user - L'oggetto CognitoUser da verificare
     * @returns {Promise<object>} - Oggetto con isValid (booleano), accessToken (stringa), idToken (stringa), refreshToken (stringa)
     */
    async verifyCognitoSession( user = currentUser ) {
        
        if ( config?.COGNITO_ENABLED !== 'Y' ) return { isValid: false }; // Se Cognito non è abilitato, esce
        if ( !user ) { user = userPool.getCurrentUser(); }
        if ( !user ) { return new Promise((resolve) => { resolve({ isValid: false }); } ); }
        
        return new Promise((resolve, reject) => {
            bDebug && console.log('verifyCognitoSession: Verifica validità sessione Cognito (INIZIO eventuale chiamata remota a AWS).');
            
            // La libreria Cognito non aggiorna automaticamente i cookie quando rinnova i token
            user.getSession(async (err, session) => {
                
                bDebug && console.log('verifyCognitoSession > getSession: Fine controllo validità sessione Cognito (FINE eventuale chiamata remota a AWS).');
                
                if ( err ) {
                    bDebug && console.error('verifyCognitoSession > getSession: Error retrieving Cognito session:', err);
                    reject(err);
                    
                } else if ( !session || !session.isValid() ) {
                    bDebug && console.error('verifyCognitoSession > getSession: Invalid Cognito session');
                    resolve({ isValid: false });
                    
                } else {
                    bDebug && console.log('verifyCognitoSession > getSession: Valid Cognito session');
                    try {
                        // si può estrarre i token dall'oggetto session, ma potrebbero essere scaduti!
                        // NO -> // const accessToken  = session.getAccessToken().getJwtToken();
                        
                        /**
                         * Controlla se il token sta per scadere e lo rinnova preventivamente
                         * @returns {Promise<string>} true se il token è stato rinnovato, false altrimenti
                         *
                         * GESTIONE DURATA DEI TOKEN E RINNOVO AUTOMATICO:
                         *
                         *    Configurazione token:
                         *    - Access Token:  durata 1 ora
                         *    - Refresh Token: durata 24 ore
                         *    - Soglia di rinnovo proattivo: 30 minuti prima della scadenza
                         *
                         *    Flusso di rinnovo basato sull'attività:
                         *
                         *   CLICK NELLA PRIMA MEZZ'ORA ( 0-30 min dall'emissione dell'access token ):
                         *       > Utente esegue login alle 10:00
                         *       > Riceve access token valido fino alle 11:00
                         *       > Utente fa un click alle 10:25 (25 minuti dopo il login)
                         *       > All'access token mancano ancora 35 minuti alla scadenza (> 30 min soglia)
                         *       > Il sistema NON rinnova il token, essendo ancora lontano dalla scadenza
                         *       > La richiesta API procede normalmente con il token esistente
                         *
                         *   CLICK NELLA SECONDA MEZZ'ORA ( 30-60 min dall'emissione dell'access token ):
                         *       > Utente esegue login alle 10:00
                         *       > Riceve access token valido fino alle 11:00
                         *       > Utente fa un click alle 10:40 (40 minuti dopo il login)
                         *       > All'access token mancano 20 minuti alla scadenza (< 30 min soglia)
                         *       > Sistema rileva che il token scadrà presto e lo rinnova proattivamente
                         *       > Utente riceve nuovo access token valido fino alle 11:40
                         *       > La richiesta API procede con il nuovo token
                         *
                         *   CLICK DOPO LA SCADENZA ( >60 min dall'emissione dell'access token ):
                         *       > Utente esegue login alle 10:00
                         *       > Riceve access token valido fino alle 11:00
                         *       > Utente inattivo per oltre un'ora
                         *       > Utente fa un click alle 11:20 (80 minuti dopo il login)
                         *       > Access token è già scaduto (da 20 minuti)
                         *       > La richiesta API fallisce con errore 401
                         *       > Sistema usa automaticamente il refresh token per ottenere nuovo access token
                         *       > Utente riceve nuovo access token valido fino alle 12:20
                         *       > La richiesta originale viene ripetuta automaticamente con il nuovo token
                         *
                         *   CLICK DOPO LA SCADENZA DEL REFRESH TOKEN ( >24h ):
                         *       > Utente esegue login alle 10:00 di lunedì
                         *       > Riceve access token valido fino alle 11:00 di lunedì
                         *       > Riceve refresh token valido fino alle 10:00 di martedì
                         *       > Utente fa un click alle 14:00 di martedì (dopo più di 24 ore)
                         *       > Sia access token che refresh token sono scaduti
                         *       > La richiesta API fallisce con errore 401
                         *       > Il tentativo di refresh fallisce (refresh token scaduto)
                         *       > Utente viene reindirizzato alla pagina di login
                         *
                         */
                        const checkCognitoTokenExpirationAndRenew = async () => {
                            
                            bDebug && console.log( 'checkCognitoTokenExpirationAndRenew: inizio verifica' );
                            
                            // Ottieni l'access token
                            const sAccessToken      = getCookieByName( config?.TOKEN_NAMES?.AccessToken  );
                            if ( !sAccessToken )    { throw new Error('No Access Token in cookies'); }
                            
                            bDebug && console.log( 'checkCognitoTokenExpirationAndRenew: access token recuperato da cookie', truncateToken( sAccessToken, 15 ) );
                            
                            // Decodifica il token per ottenere la scadenza
                            const asTokenParts      = sAccessToken.split('.');
                            if ( asTokenParts.length !== 3 ) { throw new Error('No valid Access Token in cookies'); }
                            const oPayload          = JSON.parse(atob(asTokenParts[1]));
                            if ( !oPayload.exp )    { throw new Error('No valid Access Token in cookies'); }
                            
                            // Calcola quanto tempo manca alla scadenza (in secondi)
                            const nCurrentTime      = Math.floor(Date.now() / 1000);
                            const nTimeUntilExpiry  = oPayload.exp - nCurrentTime;
                            bDebug && console.log('Scadenza Access Token: ' + new Date( oPayload.exp * 1000 ) + '. Mancano ' + nTimeUntilExpiry + ' secondi.' )
                            
                            /**
                             * Refreshes the Cognito authentication tokens using the stored refresh token.
                             *
                             * This function attempts to obtain new access and ID tokens from Amazon Cognito
                             * without requiring the user to re-authenticate.
                             * It uses the long-lived refresh token to request new short-lived tokens from the Cognito service.
                             *
                             * @async
                             * @returns {Promise<string|boolean>} A Promise that resolves to:
                             * the new access token if successful
                             * or false if the refresh cannot be attempted (missing user or refresh token).
                             *
                             * Rejects with an error if the token refresh operation fails.
                             * @throws {Error} If the Cognito session refresh fails (e.g., expired refresh token)
                             */
                            const refreshCognitoToken = async () => {
                                
                                let cognitoUser     = currentUser;
                                if ( !cognitoUser   ) { cognitoUser = userPool.getCurrentUser(); }
                                if ( !cognitoUser   ) { throw new Error('No Cognito User in userPool'); }
                                
                                const sRefreshToken = getCookieByName( config?.TOKEN_NAMES?.RefreshToken );
                                if ( !sRefreshToken ) { throw new Error('No Refresh Token in cookies'); }
                                
                                return new Promise((resolve, reject) => {
                                    
                                    // il refreshToken in forma di stringa viene convertito in un oggetto Cognito
                                    const RefreshToken = new CognitoRefreshToken({ RefreshToken: sRefreshToken });
                                    
                                    // viene inviato il RefreshToken a AWS Cognito per verifica validità
                                    // e restituirà i nuovi token Access e ID
                                    cognitoUser.refreshSession( RefreshToken, (err, cognitoSession) => {
                                        
                                        if ( err ) {
                                            console.error('refreshCognitoToken > refreshSession > Cognito Refresh Token error', err);
                                            reject(err);
                                            return;
                                        }
                                        
                                        // Estrae i nuovi token dalla sessione refreshata
                                        const sNewAccessToken = cognitoSession.getAccessToken().getJwtToken();
                                        // NON NECESSARIO // const sNewIdToken     = cognitoSession.getIdToken().getJwtToken();
                                        
                                        // Salva i token aggiornati nei cookies (NECESSARIO!!!)
                                        setCookie( config?.TOKEN_NAMES?.AccessToken, sNewAccessToken, { path: '/' ,domain: config?.COOKIE_DOMAIN });
                                        // e ID token ?
                                        bDebug && console.log('Cognito Access token rinnovato (Cognito Refresh token era ancora valido)');
                                        
                                        resolve(sNewAccessToken);
                                        
                                    });
                                });
                            }
                            
                            if ( nTimeUntilExpiry <= 0 ) {
                                // token scaduto
                                bDebug && console.log( 'checkCognitoTokenExpirationAndRenew: Cognito Access Token SCADUTO' );
                                const sNewAccessToken = await refreshCognitoToken();
                                bDebug && console.log( 'checkCognitoTokenExpirationAndRenew: Cognito Access Token RINNOVATO:' , truncateToken( sNewAccessToken, 15 ) );
                                return sNewAccessToken;
                                
                            } else if ( nTimeUntilExpiry > 0 && ( nTimeUntilExpiry < ( config.COGNITO_EXPIRE_EXTRA_SEC || 1800 ) ) ) {
                                // Se manca poco alla scadenza (default 30 min), FORZA il rinnovo del token
                                bDebug && console.log('checkCognitoTokenExpirationAndRenew: Cognito Access Token in scadenza, rinnovo preventivo.');
                                const sNewAccessToken = await refreshCognitoToken();
                                bDebug && console.log( 'checkCognitoTokenExpirationAndRenew: Cognito Access Token RINNOVATO:' , truncateToken( sNewAccessToken, 15 ) );
                                return sNewAccessToken;
                                
                            } else {
                                // manca ancora molto alla scadenza del token
                                bDebug && console.log( 'checkCognitoTokenExpirationAndRenew: Cognito Access Token VALIDO (rinnovo non necessario)' );
                                return sAccessToken;
                            }
                            
                        }
                        
                        const validAccessToken  = await checkCognitoTokenExpirationAndRenew();
                        
                        resolve({
                             isValid:        true
                            ,accessToken:    validAccessToken
                         // ,idToken:        validIdToken      // NON NECESSARIO
                         // ,refreshToken:   validRefreshToken // NON NECESSARIO
                        });
                        
                    } catch (tokenError) {
                        bDebug && console.error('verifyCognitoSession > getSession: Error extracting tokens from session:', tokenError);
                        resolve({ isValid: false });
                    }
                }
                
            });
        });
    };
    
    
    async firstCognitoLogin() {
        
        if ( config?.COGNITO_ENABLED !== 'Y' ) {
            console.warn('Cognito authentication is disabled in configuration');
            return false;
        }
        
        bDebug && console.log('firstCognitoLogin');
        
        let hubRefreshToken;
        let hubAccessToken;
        let hubIdToken;
        let sessionResult = { isValid: false };
        
        try {
            
            // Verifica presenza token nei cookie
            hubRefreshToken   = getCookieByName( config?.TOKEN_NAMES?.RefreshToken );
            hubAccessToken    = getCookieByName( config?.TOKEN_NAMES?.AccessToken  );
            hubIdToken        = getCookieByName( config?.TOKEN_NAMES?.IdToken      );
        
            if ( !hubRefreshToken ){ bDebug && console.warn('Token Refresh "'+config?.TOKEN_NAMES?.RefreshToken+'" non trovato'); }
            if ( !hubAccessToken  ){ bDebug && console.warn('Token Access  "'+config?.TOKEN_NAMES?.AccessToken +'" non trovato'); }
            if ( !hubIdToken      ){ bDebug && console.warn('Token IdToken "'+config?.TOKEN_NAMES?.IdToken     +'" non trovato'); }
            
            // Se tutti i token sono presenti nei cookie, crea una sessione Cognito
            
            if ( hubRefreshToken && hubAccessToken && hubIdToken ) {
                
                // --- (in questa parte non avviene nessuna chiamata remota, si agisce solo LOCALMENTE SUL CLIENT)
                
                // Estrae info utente dal token ID
                const validHubIdtoken       = ( typeof hubIdToken === 'string' ) ? hubIdToken.split('.')[1] : '';
                if ( !validHubIdtoken )     { throw new Error('Invalid ID token format'); }
                
                const oPayloadIdToken       = validHubIdtoken ? JSON.parse( atob( validHubIdtoken ) ) : {};
                const sub                   = oPayloadIdToken["cognito:username"];
                if ( !sub )                 { throw new Error('Invalid token (no cognito:username)'); }
                
                // Crea localmente oggetti Cognito (con i token recuperati)
                const cognitoUserFresh      = new CognitoUser(         { Username: sub, Pool: userPool });
                const cognitoIdToken        = new CognitoIdToken(      { IdToken:      hubIdToken      });
                const cognitoAccessToken    = new CognitoAccessToken(  { AccessToken:  hubAccessToken  });
                const cognitoRefreshToken   = new CognitoRefreshToken( { RefreshToken: hubRefreshToken });
                
                // Configura localmente la sessione utente con i token recuperati
                cognitoUserFresh.setSignInUserSession(
                    new CognitoUserSession({
                         IdToken:        cognitoIdToken
                        ,AccessToken:    cognitoAccessToken
                        ,RefreshToken:   cognitoRefreshToken
                    })
                );
                
                // --- (in questa parte avviene eventuale chiamata remota a Cognito su server Amazon AWS)
                
                // Verifica effettivamente la validità della sessione
                bDebug && console.log('firstCognitoLogin: Sessione creata dai cookie, verifica validità con AWS');
                // questo controllo sembra ridondante tenendo conto del primo controllo nel caso esista già un utente cognito
                // ma in realtà è necessario perché la presenza di cookie in memoria non garantisce che siano ancora validi.
                sessionResult = await this.verifyCognitoSession(cognitoUserFresh);
                
                if ( sessionResult.isValid ) {
                    // SESSIONE VALIDA
                    hubAccessToken  = sessionResult.accessToken;
                 // hubIdToken      = sessionResult.idToken;      // NON NECESSARI
                 // hubRefreshToken = sessionResult.refreshToken; // NON NECESSARI
                }
                
            }
            
        } catch ( error ) {
            console.error( 'firstCognitoLogin: Error creating Cognito session:', error );
            sessionResult = { isValid: false };
        }
        
        if ( sessionResult.isValid ) {
            bDebug && console.log('firstCognitoLogin: Valid Cognito session');
            return hubAccessToken; // ritorno l'intero access token Cognito (stringa)
            
        } else {
            bDebug && console.log('firstCognitoLogin: No valid Cognito session.');
            // RIMOSSO (proseguiamo comunque con eventuale token jwt) // Se non c'è una sessione valida, effettua logout
            // RIMOSSO (proseguiamo comunque con eventuale token jwt) // await this.logout();
            return false;
        }
        
    }
    
    /**
     * checkExistingCognitoSession - Verifica se esiste una sessione Cognito valida e la recupera
     * 
     * CASI GESTITI:
     * - CASO 1: Cognito disabilitato >>> ritorna false
     * 
     * - CASO 2: Utente già in memoria    + sessione valida                    >>> aggiorna token e ritorna access token
     * - CASO 3: Utente già in memoria    + sessione scaduta ma refresh valido >>> getSession aggiorna i token e li salva in localStorage e cookie
     * - CASO 4: Utente già in memoria    + tutti i token scaduti              >>> logout e redirect al login
     * 
     * - CASO 5: Nessun utente in memoria + token nei cookie validi            >>> crea sessione, verifica validità
     * - CASO 6: Nessun utente in memoria + token nei cookie NON validi        >>> logout e redirect al login
     * - CASO 7: Nessun utente in memoria + nessun token nei cookie            >>> logout e redirect al login
     * 
     * CHIAMATE REMOTE vs OPERAZIONI LOCALI:
     * 
     * - userPool.getCurrentUser() → operazione locale (nessuna chiamata di rete)
     * 
     * - cognitoUser.getSession() → può comportare chiamate remote se necessario refresh
     *   - Con sessione già valida: operazione locale, nessuna chiamata di rete
     *   - Con access token scaduto ma refresh token valido: CHIAMATA REMOTA per refresh
     *   - Con tutti i token scaduti: operazione locale, nessuna chiamata di rete (ritorna errore)
     * 
     * - cognitoUser.setSignInUserSession() → operazione locale (nessuna chiamata di rete)
     * 
     * OTTIMIZZAZIONI IMPLEMENTATE:
     * 1. Priorità alla verifica dell'utente esistente, riducendo creazioni non necessarie di oggetti
     * 2. Verifica completa dei token prima di utilizzarli, anche se provenienti dai cookie
     * 3. Riutilizzo intelligente dell'utente già in memoria quando disponibile
     * 4. Sincronizzazione tra localStorage e cookie per mantenere coerenza
     * 5. Sfruttamento delle capacità integrate dell'SDK Cognito per il refresh automatico dei token
     *
     * @returns {string|boolean} - Il token di accesso Cognito se la sessione è valida, altrimenti false
     *
    async checkExistingCognitoSession() {
        
        if ( config?.COGNITO_ENABLED !== 'Y' ) {
            console.warn('Cognito authentication is disabled in configuration');
            return false;
        }
        
        bDebug && console.log('checkExistingCognitoSession');
        
        let hubRefreshToken;
        let hubAccessToken;
        let hubIdToken;
        let sessionResult = { isValid: false };
        
        try {
            
            // verifica innanzitutto se esiste già un utente Cognito (in chiavi specifiche di localstorage)
            // TODO da mettere come variabile globale
            // const existingCognitoUser = userPool.getCurrentUser();
            
            // if ( existingCognitoUser ) {
                
                // (in questa parte avviene CHIAMATA REMOTA a Cognito su server Amazon AWS)
                
                // Verifica validità della sessione
                try {
                    bDebug && console.log('checkExistingCognitoSession: chiamata remota INIZIATA a AWS per verifica sessione di Cognito User trovato');
                    sessionResult = await this.verifyCognitoSession();
                    if ( sessionResult.isValid ) {
                        // SESSIONE VALIDA (potenzialmente rinnovata da getSession)
                        // Non c'è bisogno di risalvare i token, getSession lo ha già fatto in localstorage (con le sue chiavi specifiche)
                        hubAccessToken  = sessionResult.accessToken;
                     // hubIdToken      = sessionResult.idToken;      // NON NECESSARI
                     // hubRefreshToken = sessionResult.refreshToken; // NON NECESSARI
                    }
                    
                } catch (error) {
                    console.error('checkExistingCognitoSession: Error verifying existing user session:', error);
                }
                
            // }
            
            // Se non c'è un utente valido in memoria, verifica i token nei cookie
            // if ( !sessionResult.isValid ) {
                
                // Verifica presenza token nei cookie
                hubRefreshToken   = getCookieByName( config?.TOKEN_NAMES?.RefreshToken );
                hubAccessToken    = getCookieByName( config?.TOKEN_NAMES?.AccessToken  );
                hubIdToken        = getCookieByName( config?.TOKEN_NAMES?.IdToken      );
                
                // Se tutti i token sono presenti nei cookie, crea una sessione Cognito
                // (in questa parte non avviene nessun chiamata remota, si agisce solo LOCALMENTE SUL CLIENT)
                if ( hubRefreshToken && hubAccessToken && hubIdToken ) {
                    
                    // Estrae info utente dal token ID
                    const validHubIdtoken       = ( typeof hubIdToken === 'string' ) ? hubIdToken.split('.')[1] : '';
                    if ( !validHubIdtoken )     { throw new Error('Invalid ID token format'); }
                    
                    const oPayloadIdToken       = validHubIdtoken ? JSON.parse( atob( validHubIdtoken ) ) : {};
                    const sub                   = oPayloadIdToken["cognito:username"];
                    if ( !sub )                 { throw new Error('Invalid token (no cognito:username)'); }
                    
                    // Crea localmente oggetti Cognito (con i token recuperati)
                    const cognitoUserFresh      = new CognitoUser(         { Username: sub, Pool: userPool });
                    const cognitoIdToken        = new CognitoIdToken(      { IdToken:      hubIdToken      });
                    const cognitoAccessToken    = new CognitoAccessToken(  { AccessToken:  hubAccessToken  });
                    const cognitoRefreshToken   = new CognitoRefreshToken( { RefreshToken: hubRefreshToken });
                    
                    // Configura localmente la sessione utente con i token recuperati
                    cognitoUserFresh.setSignInUserSession(
                        new CognitoUserSession({
                             IdToken:        cognitoIdToken
                            ,AccessToken:    cognitoAccessToken
                            ,RefreshToken:   cognitoRefreshToken
                        })
                    );
                    
                    // Verifica effettivamente la validità della sessione
                    bDebug && console.log('checkExistingCognitoSession: Sessione creata dai cookie, verifica validità con AWS');
                    // questo controllo sembra ridondante tenendo conto del primo controllo nel caso esista già un utente cognito
                    // ma in realtà è necessario perché la presenza di cookie in memoria non garantisce che siano ancora validi.
                    sessionResult = await this.verifyCognitoSession(cognitoUserFresh);
                    
                    if ( sessionResult.isValid ) {
                        // SESSIONE VALIDA
                        hubAccessToken  = sessionResult.accessToken;
                     // hubIdToken      = sessionResult.idToken;      // NON NECESSARI
                     // hubRefreshToken = sessionResult.refreshToken; // NON NECESSARI
                    }
                    
                }
            // }
            
            if ( !sessionResult.isValid ) {
                console.error('checkExistingCognitoSession: No valid Cognito session');
                // Se non ci sono né cookie né utente Cognito, la sessione Cognito non esiste
                throw new Error('No Cognito session');
            }
            
        } catch ( error ) {
            console.error( 'checkExistingCognitoSession: Error checking existing Cognito session:', error );
            sessionResult = { isValid: false };
        }
        
        if ( sessionResult.isValid ) {
            bDebug && console.log('checkExistingCognitoSession: Valid Cognito session');
            return hubAccessToken; // ritorno l'intero access token Cognito (stringa)
            
        } else {
            // Se non c'è una sessione valida e solo se NON siamo già nella pagina di login, effettua logout
            if ( window.location.pathname !== '/login' ) {
                bDebug && console.log('checkExistingCognitoSession: pagina diversa da "login", esecuzione logout in corso...');
                await this.logout();
            }
            return false;
        }
        
    }*/
    
    /**
     * Memorizza i dati utente nel localStorage
     * @param {Object} oUserData - Dati utente da memorizzare
     * @param {string} oUserData.token - Token JWT
     * @param {string} oUserData.kUser - ID utente
     * @param {string} oUserData.username - Nome utente per autenticazione (univoco)
     * @param {string} oUserData.fullname - Nome completo utente
     * @param {string} oUserData.privileges - Privilegi utente (stringa JSON) es. "{\"FLAG_ADMIN\":\"Y\"}"
     */
    storeUserData( oUserData ) {
        
        const {
             token
            ,kUser
            ,username
            ,fullname
            ,privileges // "{\"FLAG_ADMIN\":\"Y\"}" // stringa JSON
        } = oUserData;
        
        localStorage.setItem( STORAGE_KEYS.TOKEN,       token      || '' ); bDebug && console.log('token salvato in localstorage', truncateToken(token,15));
        localStorage.setItem( STORAGE_KEYS.K_USER,      kUser      || '' );
        localStorage.setItem( STORAGE_KEYS.USERNAME,    username   || '' );
        localStorage.setItem( STORAGE_KEYS.FULL_NAME,   fullname   || '' );
        localStorage.setItem( STORAGE_KEYS.PRIVILEGES,  privileges || '' ); // "{\"FLAG_ADMIN\":\"Y\"}" // stringa JSON
        
        // Memorizza kUser originale solo la prima volta o se mancante
        if ( !localStorage.getItem( STORAGE_KEYS.ORIGINAL_K_USER ) ) {
              localStorage.setItem( STORAGE_KEYS.ORIGINAL_K_USER, kUser || '' );
        }
        
    }
    
    /**
     * Gestisce il processo di logout, indirizzando al metodo appropriato
     * in base alla configurazione (standard o Cognito)
     * @param {object} history - Oggetto history di React Router per il reindirizzamento
     * @param {function} updateAuthContext - Funzione per aggiornare lo stato di autenticazione
     */
    async logout( history = null , updateAuthContext = null ) {
        
        let bSuccessCognitoLogout = false; 
        if ( config?.COGNITO_LOGOUT_ENABLED === 'Y' ) {
            // se l'applicazione prevede Cognito proviamo a effettuare un logout nel modo di Cognito
            bSuccessCognitoLogout = await this.cognitoLogout(); // chiamata remota (opzionale) a AWS per REVOKE, pulisce localstorage e cookies
            bDebug && bSuccessCognitoLogout && console.log('Cognito Logout fine con successo');
        }
        
        if ( !bSuccessCognitoLogout ) {
            this.standardLogout(); // pulisce solo localstorage
            bDebug && console.log('Standard Logout fine con successo');
        }
        
        // Aggiorna lo stato di autenticazione React se viene fornita una funzione per farlo
        if ( updateAuthContext && ( typeof updateAuthContext === 'function' ) ) {
            updateAuthContext();
        }
        
        // solo se non si è già nella pagina di login, si viene rediretti alla login
        if ( window.location.pathname !== '/login' ) {
            
            if ( history ) {
                history.push('/login');
                
            } else {
                window.location.href = '/login';
            }
        }
        
    }
    
    /**
     * Esegue il logout standard cancellando i dati specifici dell'utente dal localStorage
     */
    standardLogout() {
        bDebug && console.log('standard Logout inizio');
        // Cancella tutti i dati utente dal localStorage
        [ ...Object.values( STORAGE_KEYS ) , ...OTHER_KEYS ].forEach( key => { // pulisce localstorage
            bDebug && console.log(key, ': ', localStorage.getItem(key));
            localStorage.removeItem( key );
        });
    }
    
    /**
     * recupera Cognito User, fa chiamata remota a AWS per REVOKE, pulisce localstorage e cookies
     * @returns {Promise<boolean>} true se il logout ha avuto successo
     */
    async cognitoLogout() {
        
        bDebug && console.log('Cognito Logout inizio');
        let bSuccessCognitoLogout = false;
        
        if ( config.COGNITO_LOGOUT_REVOKE_REFRESH === 'Y' ) {
            
            try {
                
                if ( currentUser ) {
                    
                    // Ottiene il refresh token per la revoca
                    const sRefreshToken = getCookieByName( config?.TOKEN_NAMES?.RefreshToken );
                    
                    // Revoca il refresh token se presente
                    if ( sRefreshToken ) {
                        
                        bDebug && console.log('Refresh token presente. Inizio tentativo di revoke.');
                        
                        const oParams = {
                             token:     sRefreshToken
                            ,client_id: config?.REACT_APP_AWS_APP_CLIENT_ID
                        };
                        
                        const sBodyParams = Object.entries(oParams)
                            .map( asKeysValues => asKeysValues.map( encodeURIComponent ).join('=') )
                            .join('&')
                        ;
                        
                        bDebug && console.log('url',`${ config?.COGNITO_DOMAIN }/oauth2/revoke`);
                        bDebug && console.log('body',sBodyParams);
                        
                        // CHIAMATA REMOTA A SERVER AWS !!!
                        await authFetch(`${ config?.COGNITO_DOMAIN }/oauth2/revoke` ,{
                             method:    'POST'
                            ,headers:   { 'Content-Type': 'application/x-www-form-urlencoded' }
                            ,body:      sBodyParams  // Parametri nel corpo, non nell'URL
                        });
                        bDebug && console.log('Refresh token revocato.');
                        
                    }
                    
                    currentUser.signOut(); // Esegue il signOut di Cognito
                    bSuccessCognitoLogout = true; // solo se arriva fino a qui senza errori allora è logout con successo
                    
                } else {
                    throw new Error('No Cognito User');
                }
                
            } catch ( error ) {
                console.error('Cognito logout error:', error);
            }
            
        }
        
        // in ogni caso pulisce local storage e cookies
        
        // Cancella dal localStorage tutti i dati utente (ma solo quelli specificati)
        [ ...Object.values( STORAGE_KEYS ) , ...OTHER_KEYS ].forEach( key => { // pulisce localstorage
            bDebug && console.log(key, ': ', localStorage.getItem(key));
            localStorage.removeItem( key );
        });
        
        // pulisce i cookie facendoli scadere
        // deleteCookieByName( config?.TOKEN_NAMES?.AccessToken, null, config?.COOKIE_DOMAIN );
        
        return bSuccessCognitoLogout;
        
    }
    
}

export default new AuthService();
